import HTForm from '../../_shared/form/htform';
import { observable } from 'mobx';
import { rvalueRules, percentageRule, rValue2009StandardsWarning, glazing2009StandardsWarning } from '../../_shared/validation/validationhelpers';
import { AppStore } from '../../app/app.store';

export default class CeilingForm extends HTForm {
    currentRoomNum;

    @observable era = null;
    @observable zone = null;
    @observable retrofitted2009 = false;
    @observable standards2009Compliant = null;
    @observable rValueEra = null;
    @observable numSkylights = 0;

    incrementSkylights(num, oldNum, field) {
        const path = AppStore.getFieldPath(field.path, 1),
            start = oldNum + 1;

        let i;

        for (i = start; i <= num; i++) {
            this.addSkylight(i, path);
        }
    }

    removeSkylights(num, field) {
        const path = AppStore.getFieldPath(field.path, 1),
            collection = this.$(`${path}.skylights`);

        for (let i = this.numSkylights; i > num; i -= 1) {
            collection.del(`skylight-${i}`);
        }

        this.numSkylights = num;
    }

    /**
     * Used from within loops and from the provider to add a single skylight at a time.
     * @param num
     * @param path
     */
    addSkylight(num, path) {
        const collection = this.$(`${path}.skylights`),
            exists = collection.has(`skylight-${num}`);

        if (exists) {
            return;
        }

        collection.add(this.getSkylightFields(num));

        this.numSkylights = num;
    }

    getSkylightFields(num) {
        return {
            label: `Skylight ${num}`,
            name: `skylight-${num}`,
            type: 'fieldset',
            extra: {},
            fields: [
                {
                    type: 'areafield',
                    name: `5d-1a-${num}`,
                    label: 'How big is the skylight?',
                    value: '',
                    rules: 'required',
                    extra: {
                        unit: 'm2',
                        description: () => {
                            return this.getDescription([
                                '<p>This helps us replace the right amount of ceiling surface with skylight surface.</p>',
                                `<p>${this.getHelpLink(18, 'Measuring skylights', 'help-measure_skylights')}</p>`
                            ]);
                        }
                    },
                    fields: [
                        {
                            name: `5d-1a-1-${num}`,
                            label: 'length',
                            type: 'numberfield',
                            extra: { unit: 'm' },
                            value: null,
                            placeholder: 'Length',
                            rules: 'required|numeric'
                        },
                        {
                            name: `5d-1a-2-${num}`,
                            label: 'height',
                            type: 'numberfield',
                            extra: { unit: 'm' },
                            value: null,
                            placeholder: 'Width', // Note skylight height called 'Width' for users
                            rules: 'required'
                        },
                        {
                            name: `5d-1a-3-${num}`,
                            label: 'result',
                            type: 'numberfield',
                            extra: {
                                unit: 'm2',
                                actionName: 'skylight-edit-area'
                            },
                            value: null,
                            placeholder: 'Result',
                            rules: 'required|numeric|min:0'
                        }
                    ]
                },
                {
                    type: 'radiorvaluefield',
                    name: `5d-1b-${num}`,
                    label: 'What type of glazing does the skylight have?',
                    extra: {
                        description: () => {
                            return this.getDescription([
                                '<p>This helps us understand how well the skylight retains heat.</p>',
                                `<p>${this.getHelpLink(10, 'Types of glazing', 'help-glazing_types')}</p>`
                            ]);
                        }
                    },
                    fields: [
                        {
                            name: `5d-1b-${num}-1`,
                            type: 'radio',
                            value: this.standards2009Compliant ? 'double' : 'single',
                            label:
                                'What type of glazing does the skylight have?',
                            extra: {
                                options: () => {
                                    return [
                                        {
                                            value: 'single',
                                            label: 'Single glazing'
                                        },
                                        {
                                            value: 'double',
                                            label:
                                                'Double glazing or better'
                                        }
                                    ];
                                },
                                warning: (show = false) => {
                                    return this.retrofitted2009 && show
                                        ? glazing2009StandardsWarning
                                        : false;
                                }
                            }
                        },
                        {
                            name: `5d-1b-${num}-2`,
                            type: 'rvaluefield',
                            label: 'Estimated R-value',
                            value: null,
                            rules: rvalueRules.glazing,
                            extra: {
                                era: this.rValueEra,
                                zone: this.zone,
                                element: 'skylight',
                                glazing: 'single',
                                showHint: true,
                                showIf: {
                                    '===': [
                                        { var: `5d-1b-${num}-1` },
                                        'single'
                                    ]
                                },
                                hint: 'This R-value is only an estimate — you can edit it if you know the exact value. If you know the R-value is lower you must enter that. Even if there is no insulation a wall, ceiling or floor will still have an R-value.'
                            }
                        },
                        {
                            name: `5d-1b-${num}-3`,
                            type: 'rvaluefield',
                            label: 'Estimated R-value',
                            value: 0,
                            rules: rvalueRules.glazing,
                            extra: {
                                era: this.rValueEra,
                                zone: this.zone,
                                element: 'skylight',
                                glazing: 'double',
                                warning: (show = false) => {
                                    return this.retrofitted2009 && show
                                        ? rValue2009StandardsWarning
                                        : false;
                                },
                                showIf: {
                                    '===': [
                                        { var: `5d-1b-${num}-1` },
                                        'double'
                                    ]
                                }
                            }
                        }
                    ]
                }
            ]
        };
    }

    setup() {
        return {
            fields: [
                {
                    name: 'ceilingdetails',
                    type: 'formsection',
                    label: 'Ceiling details',
                    fields: [
                        {
                            name: 'ceilingintro',
                            type: 'content',
                            extra: {
                                description: () => {
                                    return this.getDescription([
                                        '<p>In this section we’ll ask you about the ceiling, to understand what shape it is and how well it retains heat. Remember to include any other space on this level that is always open to the main living room, like an open-plan kitchen, hallway or staircase.</p>'
                                    ]);
                                }
                            }
                        },
                        {
                            name: 'floorarea',
                            type: 'numberfield',
                            disabled: true,
                            value: null,
                            label: 'Your ceiling area based on section 2 (the floor area of your main living room):',
                            extra: {
                                unit: 'm2',
                                description: () => {
                                    return 'We’ll update this as we ask more questions about your ceiling';
                                }
                            }
                            // no validation rules as this is pre-populated and disabled
                        }
                    ]
                },
                {
                    name: 'ceiling',
                    type: 'formsection',
                    label: 'Ceiling insulation',
                    extra: {},
                    fields: [
                        // 5b
                        {
                            name: '5b',
                            type: 'radio',
                            value: null,
                            label:
                                'What shape is the ceiling in your main living room?',
                            rules: 'required',
                            extra: {
                                options: () => {
                                    return [
                                        {
                                            value: 'flat',
                                            label: 'Flat'
                                        },
                                        {
                                            value: 'irregular',
                                            label:
                                                'Irregular e.g. sloping, cathedral or domed'
                                        },
                                        {
                                            value: 'mix',
                                            label:
                                                'A mix of flat and irregular'
                                        }
                                    ];
                                },
                                description: () => {
                                    return this.getDescription([
                                        '<p>We need to know if your ceiling is not flat, so we can estimate the area of the ceiling and the extra volume to add to the room.</p>'
                                    ]);
                                }
                            }
                        },
                        // 5b-2
                        {
                            name: '5b-2',
                            type: 'fieldset',
                            label:
                                'How much of the ceiling is flat vs irregular?',
                            extra: {
                                showIf: {
                                    '===': [{ var: '5b' }, 'mix']
                                },
                                description: () => {
                                    return this.getDescription([
                                        // Help #19
                                    ]);
                                }
                            },
                            fields: [
                                {
                                    name: '5b-2a',
                                    type: 'numberfield',
                                    value: null,
                                    label:
                                        'What percentage of the room has a flat ceiling?',
                                    rules: percentageRule,
                                    placeholder: 'Enter a percentage',
                                    extra: {
                                        unit: '%'
                                    },
                                    observers: [
                                        {
                                            key: 'value',
                                            call: ({ form, change }) => {
                                                this.setRemainingPercentage(
                                                    change.newValue,
                                                    form,
                                                    'ceiling.5b-2.5b-2b'
                                                );
                                            }
                                        }
                                    ]
                                },
                                {
                                    name: '5b-2b',
                                    type: 'numberfield',
                                    value: null,
                                    // no rules, this field is auto updated and disabled
                                    label:
                                        'Percentage of the room that has an irregular ceiling (calculated)',
                                    extra: {
                                        unit: '%'
                                    },
                                    disabled: true
                                }
                            ]
                        },
                        // 5c
                        {
                            name: '5c',
                            type: 'radio',
                            value: 'external',
                            label: 'What type of space is above your ceiling?',
                            rules: 'required',
                            extra: {
                                options: () => {
                                    return [
                                        {
                                            value: 'external',
                                            label:
                                                'External e.g. a balcony or a roof space'
                                        },
                                        {
                                            value: 'internal',
                                            label:
                                                'Internal e.g. another room or a neighbours apartment'
                                        },
                                        {
                                            value: 'mix',
                                            label:
                                                'A mix of external and internal'
                                        }
                                    ];
                                },
                                description: () => {
                                    return this.getDescription([
                                        '<p>This helps us understand how much heat could be lost through the ceiling.</p>',
                                        `<p>${this.getHelpLink(15, 'Understanding if a ceiling is internal or external', 'help-ceiling_types')}</p>`
                                    ]);
                                },
                                showIf: {
                                    '!==': [{ var: '5b' }, '']
                                }
                            },
                            observers: [
                                {
                                    key: 'value',
                                    call: ({ form, field, change }) => {
                                        if (change.newValue === 'internal') {
                                            this.$('skylightdetails.5d').set('value', 'no');
                                        }
                                    }
                                }
                            ]
                        },
                        // 5c-1
                        {
                            name: '5c-1',
                            type: 'radio',
                            value: 'all',
                            label:
                                'How much of your external ceiling has insulation that meets the required standard?',
                            rules: 'required',
                            extra: {
                                options: () => {
                                    return [
                                        {
                                            value: 'all',
                                            label: 'All of the ceiling'
                                        },
                                        {
                                            value: 'none',
                                            label: 'None of the ceiling'
                                        },
                                        {
                                            value: 'some',
                                            label: 'Some of the ceiling'
                                        }
                                    ];
                                },
                                description: () => {
                                    return this.getDescription([
                                        '<p>This lets us use the R-value required under the healthy homes standards, where you meet the standards.</p>',
                                        `<p>${this.getHealthyHomesLink()}</p>`
                                    ]);
                                },
                                showIf: {
                                    and: [
                                        { '!==': [{ var: '5b' }, ''] },
                                        { '===': [{ var: '5c' }, 'external'] }
                                    ]
                                },
                                warning: (show = false) => {
                                    return this.retrofitted2009 && show
                                        ? rValue2009StandardsWarning
                                        : false;
                                }
                            }
                        },
                        // 5c-1a
                        {
                            name: '5c-1a',
                            type: 'rvaluefield',
                            label: 'Estimated R-value',
                            value: null,
                            rules: rvalueRules.ceiling,
                            extra: {
                                era: this.rValueEra,
                                zone: this.zone,
                                element: 'ceiling',
                                surface: 'external',
                                standard: 'standard',
                                warning: (show = false) => {
                                    return this.retrofitted2009 && show
                                        ? rValue2009StandardsWarning
                                        : false;
                                },
                                showIf: {
                                    and: [
                                        { '!==': [{ var: '5b' }, ''] },
                                        { '===': [{ var: '5c' }, 'external'] },
                                        { '===': [{ var: '5c-1' }, 'all'] }
                                    ]
                                },
                                hint: 'This estimate includes the R-value of your ceiling insulation. This R-value is only an estimate — you can edit it if you know the exact value. If you know the R-value is lower you must enter that. Even if there is no insulation a wall, ceiling or floor will still have an R-value.'
                            }
                        },
                        // 5c-1b
                        {
                            name: '5c-1b',
                            type: 'rvaluefield',
                            label: 'Estimated R-value',
                            value: 0,
                            rules: rvalueRules.ceiling,
                            extra: {
                                era: this.rValueEra,
                                zone: this.zone,
                                element: 'ceiling',
                                surface: 'external',
                                standard: 'nonstandard',
                                showIf: {
                                    and: [
                                        { '!==': [{ var: '5b' }, ''] },
                                        { '===': [{ var: '5c' }, 'external'] },
                                        { '===': [{ var: '5c-1' }, 'none'] }
                                    ]
                                },
                                hint: 'This R-value is only an estimate — you can edit it if you know the exact value. If you know the R-value is lower you must enter that. Even if there is no insulation a wall, ceiling or floor will still have an R-value.'
                            }
                        },
                        // 5c-1c-1, 5c-1c-1p, 5c-1c-1r
                        {
                            name: '5c-1c-1',
                            type: 'percentrvaluefield',
                            label: 'What percentage of the insulation meets the standard?',
                            extra: {
                                showIf: {
                                    and: [
                                        { '!==': [{ var: '5b' }, ''] },
                                        { '===': [{ var: '5c' }, 'external'] },
                                        { '===': [{ var: '5c-1' }, 'some'] }
                                    ]
                                }
                            },
                            fields: [
                                // 5c-1c-1p
                                {
                                    name: '5c-1c-1p',
                                    type: 'numberfield',
                                    label:
                                        'What percentage of the external ceiling has insulation that meets the standard?',
                                    value: null,
                                    rules: percentageRule,
                                    placeholder: 'Enter a percentage',
                                    extra: {
                                        unit: '%'
                                    },
                                    observers: [
                                        {
                                            key: 'value',
                                            call: ({ form, change }) => {
                                                this.setRemainingPercentage(
                                                    change.newValue,
                                                    form,
                                                    'ceiling.5c-1c-2.5c-1c-2p'
                                                );
                                            }
                                        }
                                    ]
                                },
                                // 5c-1c-1r
                                {
                                    name: '5c-1c-1r',
                                    type: 'rvaluefield',
                                    label: 'Estimated R-value ',
                                    value: null,
                                    rules: rvalueRules.ceiling,
                                    extra: {
                                        era: this.rValueEra,
                                        zone: this.zone,
                                        element: 'ceiling',
                                        surface: 'external',
                                        standard: 'standard',
                                        parentWarning: (show = false) => {
                                            return this.retrofitted2009 && show
                                                ? rValue2009StandardsWarning
                                                : false;
                                        }
                                    }
                                }
                            ]
                        },
                        // 5c-1c-2, 5c-1c-2p, 5c-1c-2r
                        {
                            name: '5c-1c-2',
                            type: 'percentrvaluefield',
                            label: 'Percentage of the insulation that does not meet the standard',
                            extra: {
                                showIf: {
                                    and: [
                                        { '!==': [{ var: '5b' }, ''] },
                                        { '===': [{ var: '5c' }, 'external'] },
                                        { '===': [{ var: '5c-1' }, 'some'] }
                                    ]
                                }
                            },
                            fields: [
                                // 5c-2c-1p
                                {
                                    name: '5c-1c-2p',
                                    type: 'numberfield',
                                    label:
                                        'Percentage of the external ceiling with insulation that does not meet the standard (calculated)',
                                    value: null,
                                    rules: percentageRule,
                                    extra: {
                                        unit: '%'
                                    },
                                    disabled: true
                                },
                                // 5c-1c-2r
                                {
                                    name: '5c-1c-2r',
                                    type: 'rvaluefield',
                                    label: 'Estimated R-value ',
                                    value: null,
                                    rules: rvalueRules.ceiling,
                                    extra: {
                                        era: this.rValueEra,
                                        zone: this.zone,
                                        element: 'ceiling',
                                        surface: 'external',
                                        standard: 'nonstandard',
                                        hint: 'These estimates include the R-value of your ceiling insulation (where applicable). These R-values are only an estimate — you can edit them if you know the exact value. If you know an R-value is lower you must enter that. Even if there is no insulation a wall, ceiling or floor will still have an R-value.'
                                    }
                                }
                            ]
                        },
                        // 5c-2
                        {
                            name: '5c-2',
                            type: 'rvaluefield',
                            label: 'Estimated R-value ',
                            value: 0,
                            rules: rvalueRules.ceiling,
                            extra: {
                                era: this.rValueEra,
                                zone: this.zone,
                                element: 'ceiling',
                                surface: 'internal',
                                standard: 'standard',
                                showIf: {
                                    and: [
                                        {
                                            or: [
                                                { '===': [{ var: '5b' }, 'flat'] },
                                                { '===': [{ var: '5b' }, 'irregular'] }
                                            ]
                                        },
                                        { '===': [{ var: '5c' }, 'internal'] }
                                    ]
                                },
                                hint: 'This R-value is only an estimate — you can edit it if you know the exact value. Most houses do not have insulation in internal ceilings so the tool assumes this. If you know the R-value is lower you must enter that. Even if there is no insulation a wall, ceiling or floor will still have an R-value.'
                            }
                        },
                        // 5c-3c
                        {
                            name: '5c-3c',
                            type: 'content',
                            label:
                                'How much of the ceiling is internal and how much is external?',
                            extra: {
                                description: () => {
                                    return this.getDescription([
                                        `<p>${this.getHelpLink(16, 'Finding the percentage of internal and external ceiling', 'help-mixed_ceilings')}</p>`
                                    ]);
                                },
                                showIf: {
                                    and: [
                                        { '!==': [{ var: '5b' }, ''] },
                                        { '===': [{ var: '5c' }, 'mix'] }
                                    ]
                                }
                            }
                        },
                        // 5c-3: 5c-3p, 5c-3r
                        {
                            name: '5c-3',
                            type: 'percentrvaluefield',
                            label: 'What percentage of the ceiling is internal?',
                            extra: {
                                showIf: {
                                    and: [
                                        { '!==': [{ var: '5b' }, ''] },
                                        { '===': [{ var: '5c' }, 'mix'] }
                                    ]
                                },
                                hint: 'This R-value is only an estimate — you can edit it if you know the exact value. Most houses do not have insulation in internal ceilings so the tool assumes this. If you know the R-value is lower you must enter that. Even if there is no insulation a wall, ceiling or floor will still have an R-value.'
                            },
                            fields: [
                                // 5c-3p
                                {
                                    name: '5c-3p',
                                    type: 'numberfield',
                                    label:
                                        'What percentage of the ceiling is internal?',
                                    value: null,
                                    placeholder: 'Enter a percentage',
                                    rules: percentageRule,
                                    extra: {
                                        unit: '%'
                                    },
                                    observers: [
                                        {
                                            key: 'value',
                                            call: ({ form, change }) => {
                                                this.setRemainingPercentage(
                                                    change.newValue,
                                                    form,
                                                    [
                                                        'ceiling.5c-3a-1.5c-3a-1p',
                                                        'ceiling.5c-3a-2.5c-3a-2p'
                                                    ]
                                                );
                                            }
                                        }
                                    ]
                                },
                                // 5c-3r
                                {
                                    name: '5c-3r',
                                    type: 'rvaluefield',
                                    label: 'Estimated R-value',
                                    value: null,
                                    rules: rvalueRules.ceiling,
                                    extra: {
                                        era: this.rValueEra,
                                        zone: this.zone,
                                        element: 'ceiling',
                                        surface: 'internal',
                                        standard: 'nonstandard'
                                    }
                                }
                            ]
                        },
                        // 5c-3a
                        {
                            name: '5c-3a',
                            type: 'radio',
                            value: 'all',
                            label:
                                'How much of your external ceiling has insulation that meets the required standard?',
                            rules: 'required',
                            extra: {
                                options: () => {
                                    return [
                                        {
                                            value: 'all',
                                            label: 'All of the ceiling'
                                        },
                                        {
                                            value: 'none',
                                            label: 'None of the ceiling'
                                        },
                                        {
                                            value: 'some',
                                            label: 'Some of the ceiling'
                                        }
                                    ];
                                },
                                description: () => {
                                    return this.getDescription([
                                        '<p>This helps us estimate the R-value where you meet the standards.</p>',
                                        `<p>${this.getHealthyHomesLink()}</p>`
                                    ]);
                                },
                                showIf: {
                                    and: [
                                        { '===': [{ var: '5c' }, 'mix'] },
                                        { '!==': [{ var: '5b' }, ''] }
                                    ]
                                },
                                warning: (show = false) => {
                                    return this.retrofitted2009 && show
                                        ? rValue2009StandardsWarning
                                        : false;
                                }
                            }
                        },
                        // 5c-3a-1: 5c-3a-1p, 5c-3a-1r
                        {
                            name: '5c-3a-1',
                            type: 'percentrvaluefield',
                            label: 'Percentage of the ceiling that is external space',
                            extra: {
                                showIf: {
                                    and: [
                                        { '!==': [{ var: '5b' }, ''] },
                                        { '===': [{ var: '5c' }, 'mix'] },
                                        { '===': [{ var: '5c-3a' }, 'all'] }
                                    ]
                                },
                                hint: 'This R-value is only an estimate — you can edit it if you know the exact value. If you know the R-value is lower you must enter that. Even if there is no insulation a wall, ceiling or floor will still have an R-value.'
                            },
                            fields: [
                                // 5c-3a-1p
                                {
                                    name: '5c-3a-1p',
                                    type: 'numberfield',
                                    label:
                                        'Percentage of the ceiling that is external space',
                                    value: null,
                                    rules: percentageRule,
                                    extra: {
                                        unit: '%'
                                    },
                                    disabled: true
                                },
                                // 5c-3a-1r
                                {
                                    name: '5c-3a-1r',
                                    type: 'rvaluefield',
                                    label: 'Estimated R-value',
                                    value: null,
                                    rules: rvalueRules.ceiling,
                                    extra: {
                                        era: this.rValueEra,
                                        zone: this.zone,
                                        element: 'ceiling',
                                        surface: 'external',
                                        standard: 'standard',
                                        parentWarning: (show = false) => {
                                            return this.retrofitted2009 && show
                                                ? rValue2009StandardsWarning
                                                : false;
                                        }
                                    }
                                }
                            ]
                        },
                        // 5c-3a-2: 5c-3a-2p, 5c-3a-2r
                        {
                            name: '5c-3a-2',
                            type: 'percentrvaluefield',
                            label: 'Percentage of the ceiling that is external space',
                            extra: {
                                showIf: {
                                    and: [
                                        { '!==': [{ var: '5b' }, ''] },
                                        { '===': [{ var: '5c' }, 'mix'] },
                                        { '===': [{ var: '5c-3a' }, 'none'] }
                                    ]
                                },
                                hint: 'This estimate includes the R-value of your ceiling insulation. This R-value is only an estimate — you can edit it if you know the exact value. If you know the R-value is lower you must enter that. Even if there is no insulation a wall, ceiling or floor will still have an R-value.'
                            },
                            fields: [
                                // 5c-3a-2p
                                {
                                    name: '5c-3a-2p',
                                    type: 'numberfield',
                                    label:
                                        'Percentage of the ceiling that is external space',
                                    value: null,
                                    rules: percentageRule,
                                    extra: {
                                        unit: '%'
                                    },
                                    disabled: true
                                },
                                // 5c-3a-2r
                                {
                                    name: '5c-3a-2r',
                                    type: 'rvaluefield',
                                    label: 'Estimated R-value',
                                    value: null,
                                    rules: rvalueRules.ceiling,
                                    extra: {
                                        era: this.rValueEra,
                                        zone: this.zone,
                                        element: 'ceiling',
                                        surface: 'external',
                                        standard: 'nonstandard'
                                    }
                                }
                            ]
                        },
                        // 5c-3a-3-1: 5c-3a-3-1p, 5c-3a-3-1r
                        {
                            name: '5c-3a-3-1',
                            type: 'percentrvaluefield',
                            label: 'What percentage of the external ceiling has insulation that meets the standard?',
                            extra: {
                                showIf: {
                                    and: [
                                        { '!==': [{ var: '5b' }, ''] },
                                        { '===': [{ var: '5c' }, 'mix'] },
                                        { '===': [{ var: '5c-3a' }, 'some'] }
                                    ]
                                }
                            },
                            fields: [
                                // 5c-3a-3-1p
                                {
                                    name: '5c-3a-3-1p',
                                    type: 'numberfield',
                                    label:
                                        'What percentage of the external ceiling has insulation that meets the standard?',
                                    value: null,
                                    rules: percentageRule,
                                    placeholder: 'Enter a percentage',
                                    extra: {
                                        unit: '%'
                                    },
                                    observers: [
                                        {
                                            key: 'value',
                                            call: ({ form, change }) => {
                                                this.setRemainingPercentage(
                                                    change.newValue,
                                                    form,
                                                    [
                                                        'ceiling.5c-3a-3-2.5c-3a-3-2p'
                                                    ]
                                                );
                                            }
                                        }
                                    ]
                                },
                                // 5c-3a-3-1r
                                {
                                    name: '5c-3a-3-1r',
                                    type: 'rvaluefield',
                                    label: 'Estimated R-value',
                                    value: null,
                                    rules: rvalueRules.ceiling,
                                    extra: {
                                        era: this.rValueEra,
                                        zone: this.zone,
                                        element: 'ceiling',
                                        surface: 'external',
                                        standard: 'standard',
                                        parentWarning: (show = false) => {
                                            return this.retrofitted2009 && show
                                                ? rValue2009StandardsWarning
                                                : false;
                                        }
                                    }
                                }
                            ]
                        },
                        // 5c-3a-3-2: 5c-3a-3-2p, 5c-3a-3-2r
                        {
                            name: '5c-3a-3-2',
                            type: 'percentrvaluefield',
                            label: 'Percentage of the external ceiling that has no insulation, or insulation that does not meet the standard',
                            extra: {
                                showIf: {
                                    and: [
                                        { '===': [{ var: '5c' }, 'mix'] },
                                        { '===': [{ var: '5c-3a' }, 'some'] }
                                    ]
                                },
                                hint: 'These estimates include the R-value of your ceiling insulation (where applicable). These R-values are only an estimate — you can edit them if you know the exact value. If you know an R-value is lower you must enter that. Even if there is no insulation a wall, ceiling or floor will still have an R-value.'
                            },
                            fields: [
                                {
                                    name: '5c-3a-3-2p',
                                    type: 'numberfield',
                                    label:
                                        'Percentage of the external ceiling that has no insulation, or insulation that does not meet the standard ',
                                    value: null,
                                    rules: percentageRule,
                                    extra: {
                                        unit: '%'
                                    },
                                    disabled: true
                                },
                                {
                                    name: '5c-3a-3-2r',
                                    type: 'rvaluefield',
                                    label: 'Estimated R-value',
                                    value: null,
                                    rules: rvalueRules.ceiling,
                                    extra: {
                                        era: this.rValueEra,
                                        zone: this.zone,
                                        element: 'ceiling',
                                        surface: 'external',
                                        standard: 'nonstandard'
                                    }
                                }
                            ]
                        }
                    ]
                },
                // Skylights
                {
                    name: 'skylightdetails',
                    type: 'formsection',
                    label: 'Skylight details',
                    extra: {
                        showIf: {
                            and: [
                                { '!==': [{ var: '5b' }, ''] },
                                {
                                    or: [
                                        { '===': [{ var: '5c' }, 'external'] },
                                        { '===': [{ var: '5c' }, 'mix'] }
                                    ]
                                }
                            ]
                        }
                    },
                    fields: [
                        {
                            name: '5d',
                            type: 'radio',
                            value: 'no',
                            label: 'Does the ceiling have any skylights?',
                            extra: {
                                options: () => {
                                    return [
                                        {
                                            value: 'no',
                                            label: 'No'
                                        },
                                        {
                                            value: 'yes',
                                            label: 'Yes'
                                        }
                                    ];
                                },
                                description: () => {
                                    return this.getDescription([
                                        '<p>This helps us understand more about the surfaces heat can escape through.</p>'
                                    ]);
                                }
                            },
                            observers: [
                                {
                                    key: 'value',
                                    call: ({ form, field, change }) => {
                                        const path = AppStore.getFieldPath(field.path, 1);
                                        if (change.newValue === 'yes') {
                                            this.incrementSkylights(1, 0, field);
                                        } else {
                                            this.removeSkylights(0, field);
                                            this.$(`${path}.5d-1`).set('value', 1);
                                        }
                                    }
                                }
                            ]
                        },
                        {
                            name: '5d-1',
                            type: 'stepper',
                            value: 0,
                            label: 'How many skylights does the ceiling have?',
                            rules: 'required|between:0,2',
                            extra: {
                                min: 1,
                                max: 2,
                                description: () => {
                                    return this.getDescription([
                                        `<p>${this.getHelpLink(17, 'Counting skylights and panes', 'help-count_skylights')}</p>`
                                    ]);
                                },
                                showIf: {
                                    '===': [{ var: '5d' }, 'yes']
                                }
                            },
                            observers: [
                                {
                                    key: 'value',
                                    call: ({ form, field, change }) => {
                                        if (change.newValue > change.oldValue) {
                                            this.incrementSkylights(change.newValue, change.oldValue, field);
                                        } else {
                                            this.removeSkylights(change.newValue, field);
                                        }
                                    }
                                }
                            ]
                        },
                        {
                            name: 'skylights',
                            type: 'collection',
                            extra: {
                                showIf: {
                                    and: [
                                        { '===': [{ var: '5d' }, 'yes'] }
                                    ]
                                }
                            },
                            fields: []
                        }
                    ]
                }
            ]
        };
    }
}
